import React, { useState, useEffect } from 'react';
import { useParams, useHistory } from 'react-router-dom';
import API_BASE_URL, { getImageUrl } from '../config';
import { compressImage } from '../utils/imageUtils';
import './GuestJoin.css';

const GuestJoin: React.FC = () => {
    const { token } = useParams<{ token: string }>();
    const history = useHistory();
    const [name, setName] = useState('');
    const [phone, setPhone] = useState('');
    const [password, setPassword] = useState('');
    const [passwordTouched, setPasswordTouched] = useState(false);
    const [relationship, setRelationship] = useState('');
    const [profilePhoto, setProfilePhoto] = useState<File | null>(null);
    const [previewUrl, setPreviewUrl] = useState<string>('');
    const [loading, setLoading] = useState(false);
    const [status, setStatus] = useState<'idle' | 'checking' | 'pending' | 'approved' | 'rejected'>('idle');
    const [message, setMessage] = useState('');
    const [savedProfilePhoto, setSavedProfilePhoto] = useState('');

    useEffect(() => {
        const checkStatus = async () => {
            const savedPhone = localStorage.getItem('guest_request_phone');
            if (savedPhone) {
                setStatus('checking');
                try {
                    const response = await fetch(`${API_BASE_URL}/api/guests/status/${token}/${savedPhone}`);
                    const data = await response.json();
                    if (response.ok) {
                        setStatus(data.status);
                        setName(data.name);
                        setMessage(data.message);
                        if (data.profilePhoto) {
                            setSavedProfilePhoto(data.profilePhoto);
                        }
                        return;
                    }
                } catch (err) {
                    console.error('Status check error:', err);
                }
                setStatus('idle');
            }
        };
        checkStatus();
    }, [token]);

    useEffect(() => {
        if (!passwordTouched) {
            setPassword(phone);
        }
    }, [phone, passwordTouched]);

    const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
        if (e.target.files && e.target.files[0]) {
            const file = e.target.files[0];
            const url = URL.createObjectURL(file);
            setPreviewUrl(url);
            try {
                let finalFile = file;
                if (file.size > 2 * 1024 * 1024) {
                    finalFile = await compressImage(file, 2);
                }
                setProfilePhoto(finalFile);
            } catch (err) {
                console.error('Error processing image:', err);
                setProfilePhoto(file);
            }
        }
    };

    const removePreview = () => {
        setPreviewUrl('');
        setProfilePhoto(null);
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        if (!/^\d{10}$/.test(phone)) {
            alert('Please enter a valid 10-digit mobile number');
            return;
        }

        setLoading(true);
        try {
            const formData = new FormData();
            formData.append('name', name);
            formData.append('phone', phone);
            formData.append('password', password);
            formData.append('relationship', relationship);
            if (profilePhoto) {
                formData.append('profilePhoto', profilePhoto);
            }

            const response = await fetch(`${API_BASE_URL}/api/guests/join/${token}`, {
                method: 'POST',
                body: formData,
            });

            const data = await response.json();

            if (response.ok) {
                localStorage.setItem('guest_request_phone', phone);
                setStatus('pending');
                setMessage(data.message);

                // Extra careful: Extract photo from response
                const returnedPhoto = data.profilePhoto ||
                    (data.guest && data.guest.profilePhoto) ||
                    (data.data && data.data.profilePhoto);

                if (returnedPhoto) {
                    setSavedProfilePhoto(returnedPhoto);
                }
            } else {
                alert(data.message || 'Error joining');
            }
        } catch (err) {
            console.error('Join error:', err);
            alert('Server error');
        } finally {
            setLoading(false);
        }
    };

    if (status === 'checking') {
        return (
            <div className="guest-join-container">
                <div className="loader">✨ Recognizing your invite...</div>
            </div>
        );
    }

    if (status === 'pending') {
        return (
            <div className="guest-join-container">
                <div className="guest-join-card pending-state">
                    {savedProfilePhoto ? (
                        <div className="profile-photo-preview-container">
                            <div className="profile-photo-preview">
                                <img src={getImageUrl(savedProfilePhoto)} alt="Profile" />
                            </div>
                        </div>
                    ) : (
                        <div className="status-icon">⏳</div>
                    )}
                    <h1>Request Sent!</h1>
                    <p>Dear {name}, your request to join the wedding application has been sent to the admin.</p>
                    <p className="status-note">{message || 'Please wait for approval. Once approved, you can use your mobile number and password to access the app.'}</p>
                    <button className="join-btn secondary" onClick={() => {
                        localStorage.removeItem('guest_request_phone');
                        setStatus('idle');
                    }}>Use different number</button>
                </div>
            </div>
        );
    }

    if (status === 'approved') {
        return (
            <div className="guest-join-container">
                <div className="guest-join-card approved-state">
                    {savedProfilePhoto ? (
                        <div className="profile-photo-preview-container">
                            <div className="profile-photo-preview">
                                <img src={getImageUrl(savedProfilePhoto)} alt="Profile" />
                            </div>
                        </div>
                    ) : (
                        <div className="status-icon">🎉</div>
                    )}
                    <h1>Access Granted!</h1>
                    <p>Welcome back {name}! Your request has been approved.</p>
                    <button className="join-btn" onClick={() => history.push('/login')}>Go to Dashboard</button>
                </div>
            </div>
        );
    }

    if (status === 'rejected') {
        return (
            <div className="guest-join-container">
                <div className="guest-join-card rejected-state">
                    <div className="status-icon">🚫</div>
                    <h1>Request Refused</h1>
                    <p>We're sorry {name}, your request for access was not accepted at this time.</p>
                    <button className="join-btn" onClick={() => {
                        localStorage.removeItem('guest_request_phone');
                        setStatus('idle');
                    }}>Try again</button>
                </div>
            </div>
        );
    }

    return (
        <div className="guest-join-container">
            <div className="guest-join-card">
                <div className="header-decoration">✨</div>
                <h1>Wedding Guest Request</h1>
                <p>Please provide your details to request access to the wedding management app.</p>

                <form onSubmit={handleSubmit}>
                    <div className="form-group">
                        <label>Your Full Name</label>
                        <input
                            type="text"
                            value={name}
                            onChange={(e) => setName(e.target.value)}
                            placeholder="Enter your name"
                            required
                        />
                    </div>

                    <div className="form-group">
                        <label>Mobile Number</label>
                        <input
                            type="tel"
                            value={phone}
                            onChange={(e) => setPhone(e.target.value.replace(/\D/g, '').slice(0, 10))}
                            placeholder="10 digit mobile number"
                            required
                        />
                        <small>This will be your username</small>
                    </div>

                    <div className="form-group">
                        <label>Password</label>
                        <input
                            type="password"
                            value={password}
                            onChange={(e) => {
                                setPassword(e.target.value);
                                setPasswordTouched(true);
                            }}
                            placeholder="Enter password"
                            required
                        />
                        <small>By default, your mobile number is your password</small>
                    </div>

                    <div className="form-group">
                        <label>Relationship with Couple</label>
                        <select
                            value={relationship}
                            onChange={(e) => setRelationship(e.target.value)}
                            required
                        >
                            <option value="">Select Relationship</option>
                            <option value="Family">Family</option>
                            <option value="Friend">Friend</option>
                            <option value="Relative">Relative</option>
                            <option value="Colleague">Colleague</option>
                            <option value="Other">Other</option>
                        </select>
                    </div>

                    <div className="form-group">
                        <label>Profile Photo</label>
                        <div className="photo-upload-wrapper">
                            <input
                                type="file"
                                id="profilePhoto"
                                accept="image/*"
                                onChange={handleFileChange}
                                style={{ display: 'none' }}
                            />
                            <label htmlFor="profilePhoto" className="photo-upload-label">
                                {previewUrl ? (
                                    <div className="preview-container">
                                        <img src={previewUrl} alt="Preview" />
                                        <div className="preview-overlay">Change Photo</div>
                                        <button type="button" className="remove-preview" onClick={(e) => {
                                            e.preventDefault();
                                            removePreview();
                                        }}>&times;</button>
                                    </div>
                                ) : (
                                    <div className="upload-placeholder">
                                        <span className="upload-icon">📸</span>
                                        <span>Click to add photo</span>
                                    </div>
                                )}
                            </label>
                        </div>
                    </div>

                    <button type="submit" className="join-btn" disabled={loading}>
                        {loading ? 'Sending Request...' : 'Send Access Request'}
                    </button>
                </form>
            </div>
        </div>
    );
};

export default GuestJoin;
